import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { quizApi } from "@/lib/quizApi";
import { loadSession, saveSession } from "@/lib/session";
import { BrandHeader } from "@/components/BrandHeader";

export default function Instructions() {
  const { quizId = "" } = useParams();
  const navigate = useNavigate();
  const session = loadSession();

  const quiz = useQuery({
    queryKey: ["publicQuiz", quizId],
    queryFn: () => quizApi.getPublic(quizId),
    enabled: !!quizId,
  });

  // If we skipped ID-upload (quiz doesn't require it), an attempt may not exist yet.
  const ensureAttempt = useMutation({
    mutationFn: async () => {
      if (session?.attemptId) return session.attemptId;
      const a = await quizApi.createAttempt(quizId);
      saveSession({ ...(session!), attemptId: a.attemptId });
      return a.attemptId;
    },
    onSuccess: (attemptId) => navigate(`/quiz/${quizId}/attempt/${attemptId}`),
  });

  if (!session) return <Centered>Session expired. Please register again.</Centered>;
  if (quiz.isLoading) return <Centered>Loading…</Centered>;
  const q = quiz.data!;

  return (
    <>
      <BrandHeader />
      <main className="mx-auto max-w-3xl p-6">
      <div className="card p-8">
        <div className="flex items-start justify-between">
          <h1 className="text-2xl font-bold text-brand-ink">{q.title}</h1>
          <div className="text-right">
            <div className="text-2xl font-bold text-brand">{q.durationMinutes}:00</div>
            <div className="text-xs text-brand-ink/60">Quiz Time</div>
          </div>
        </div>

        {q.instructions ? (
          <div
            className="prose prose-sm mt-6 max-w-none text-brand-ink"
            dangerouslySetInnerHTML={{ __html: q.instructions }}
          />
        ) : (
          <ol className="mt-6 list-decimal space-y-3 pl-6 text-sm">
            <li><b>Full-Screen Mode:</b> The exam must be taken in full-screen mode. Exiting full screen will deduct 1 point.</li>
            <li><b>Camera Access:</b> Camera access is mandatory to begin and submit the exam.</li>
            <li><b>Tab Switching:</b> Do not switch tabs. Switching tabs will deduct 1 point per switch and may lead to disqualification.</li>
            <li><b>Time Limit:</b> Complete the exam within the allotted time. Late submissions will not be accepted.</li>
            <li><b>Instructions:</b> Read each question carefully.</li>
            <li><b>Answer Submission:</b> Submit before the timer expires. No changes can be made after submission.</li>
            <li><b>Desktop / Laptop:</b> Use Chrome browser only. Any other browser will disqualify the attempt.</li>
          </ol>
        )}

        <div className="mt-8 flex justify-end">
          <button className="btn-primary" onClick={() => ensureAttempt.mutate()} disabled={ensureAttempt.isPending}>
            {ensureAttempt.isPending ? "Starting…" : "Start Exam →"}
          </button>
        </div>
      </div>
      </main>
    </>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="grid min-h-screen place-items-center p-6 text-brand-ink/60">{children}</main>;
}
